import React, { useState, useCallback, useEffect } from 'react';

import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import MainNavigation from './MainNavigation';
import RoleSelect from './RoleSelect';
import { Box } from '@mui/material';

// Menus and popovers — every Select dropdown among them — must not lock page scroll. MUI's
// default scroll-lock removes the scrollbar and pads the body to compensate, which shifts the
// centered layout when a dropdown opens. We reserve the scrollbar gutter in CSS instead
// (scrollbar-gutter: stable, assets/style.css), so the lock is unnecessary; disable it globally.
// Menu/Popover pass their own disableScrollLock down to Modal, so it must be set on each.
const theme = createTheme({
    components: {
        MuiMenu: { defaultProps: { disableScrollLock: true } },
        MuiPopover: { defaultProps: { disableScrollLock: true } },
    },
})
import LoginDialog from './LoginDialog';
import Blocked from './Blocked';
import ErrorBoundary from './ErrorBoundary';
import { apiGetRoles, apiKeepalive, apiLogOut, isApiError, Role } from './Api';
import { Capability } from './generated/capabilities';
import { useChainedCallback, useRegisteredErrorCallback, useRegisteredLockingCallback } from './CallCenter';
import useSessionExpiry from './useSessionExpiry';
import SessionExpiryDialog from './SessionExpiryDialog';


export default function MainImplementation({initiallyLoggedIn, sessionWarningSeconds}: {
        initiallyLoggedIn: boolean, sessionWarningSeconds: number}) {
    const [loggedIn, setLoggedIn] = useState(initiallyLoggedIn)
    const [vismaConnected, setVismaConnected] = useState(true)
    const [roles, setRoles] = useState<Role[] | null>(null)
    // Distinct from `roles === null`: that means the call is still out, this means it came back
    // failed. Without the distinction a failed roles call left the page blank for ever.
    const [rolesFailed, setRolesFailed] = useState(false)
    const [activeRoleId, setActiveRoleId] = useState<number | null>(null)
    const [capabilities, setCapabilities] = useState<Capability[]>([])
    const [scope, setScope] = useState<[string, string][]>([])
    const [prepareAllScopes, setPrepareAllScopes] = useState(false)
    // Null until the roles call lands, and for sessions that predate the server storing it.
    const [userName, setUserName] = useState<string | null>(null)
    const [pending, setPending] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [errorDetail, setErrorDetail] = useState<string | null>(null)
    const [stayingLoggedIn, setStayingLoggedIn] = useState(false)
    useRegisteredLockingCallback(setPending)

    // The countdown runs only while logged in, and only speaks up for the last few minutes.
    // Reaching zero with the page open means the session went while nobody was looking, so show
    // the login screen rather than let the next click fail.
    const secondsLeft = useSessionExpiry(
        loggedIn, sessionWarningSeconds, useCallback(() => setLoggedIn(false), []))

    const handleStayLoggedIn = useCallback(async () => {
        // Deliberately outside the call chain: this must not raise the full-screen backdrop over
        // a dialog whose own button already shows that it is working.
        setStayingLoggedIn(true)
        try {
            await apiKeepalive()
        } catch {
            // A dead session comes back as 401, which the error bus has already turned into the
            // login screen. Nothing left to do here.
        } finally {
            setStayingLoggedIn(false)
        }
    }, [])

    const loadRoles = useCallback(() => {
        setRolesFailed(false)
        apiGetRoles()
            .then(info => { setRoles(info.roles); setActiveRoleId(info.active_role_id); setCapabilities(info.capabilities); setScope(info.scope); setPrepareAllScopes(info.prepare_all_scopes); setUserName(info.user_name) })
            // The error bus has already shown or classified this; all that is left is to stop
            // waiting for an answer that is not coming, so the screen can offer a way out.
            .catch(() => setRolesFailed(true))
    }, [])

    useEffect(() => {
        if (loggedIn) {
            loadRoles()
        }
    }, [loggedIn, loadRoles])

    // Every blocked screen's "Försök igen". The blocking states are all conclusions drawn from a
    // failed call, so clearing them and asking again is the whole retry: if the cause is still
    // there the same screen comes back, and if an administrator has fixed it in the meantime the
    // app carries on without a reload.
    const handleRetry = useCallback(() => {
        setVismaConnected(true)
        setRoles(null)
        loadRoles()
    }, [loadRoles])

    const handleError = useCallback(
        // The error bus carries `unknown` (a thrown value can be anything in JS), so
        // narrow with isApiError before dispatching. The gate conditions change
        // top-level app state; everything else — unexpected 500s, and any non-ApiError
        // throw — is surfaced in a snackbar so it is never silently swallowed. Richer
        // categories (conflict/transient/...) get their own branches in later phases.
        (error: unknown) => {
            if (!isApiError(error)) {
                setErrorMessage("Något gick fel.")
                setErrorDetail(null)
            } else if (error.status === 401) {
                setLoggedIn(false)
            } else if (error.status === 503 && error.code === "visma_not_connected") {
                setVismaConnected(false)
            } else if (error.status === 403 && error.code === "no_role") {
                setRoles([])
            } else if (error.status === 403 && error.code === "role_not_selected") {
                setActiveRoleId(null)
            } else {
                setErrorMessage(error.message)
                const ref = [
                    error.vismaRef ? `Referens: ${error.vismaRef}` : null,
                    error.vismaCode ? `kod ${error.vismaCode}` : null,
                ].filter(Boolean).join(' · ')
                setErrorDetail(ref || null)
            }
        },
        []
    )
    useRegisteredErrorCallback(handleError)

    const handleLogOut = useChainedCallback(
        async () => {
            // The local session ends whatever the server answers. This is the escape hatch from
            // the blocked screens, so it must not be possible for a failing request to keep
            // somebody on one of them.
            try {
                await apiLogOut()
            } finally {
                setLoggedIn(false)
                setVismaConnected(true)
                setRoles(null)
                setRolesFailed(false)
                setActiveRoleId(null)
            }
        },
        true,
        []
    )

    function content() {
        if (!loggedIn) {
            return <LoginDialog/>
        }
        if (!vismaConnected) {
            return (
                <Blocked onRetry={handleRetry} onLogOut={handleLogOut}>
                    <p>Den här föreningen är inte ansluten till Visma än.</p>
                    <p>En administratör behöver ansluta föreningen innan den kan användas. Är du
                       inloggad på fel förening kan du logga ut och välja en annan i Visma.</p>
                </Blocked>
            )
        }
        if (rolesFailed) {
            return (
                <Blocked onRetry={handleRetry} onLogOut={handleLogOut}>
                    <p>Eko kunde inte läsa dina behörigheter.</p>
                    <p>Försök igen. Står det still kan du logga ut och logga in på nytt.</p>
                </Blocked>
            )
        }
        if (roles === null) {
            return null  // roles still loading
        }
        if (roles.length === 0) {
            return (
                <Blocked onRetry={handleRetry} onLogOut={handleLogOut}>
                    <p>Du har inte tilldelats någon roll i Eko för det här företaget.</p>
                    <p>Kontakta en administratör för att få behörighet. Har du nyss fått en roll
                       räcker det att försöka igen.</p>
                </Blocked>
            )
        }
        if (activeRoleId === null) {
            return <RoleSelect roles={roles} userName={userName}/>
        }
        return (
            <MainNavigation
                onLogOut={handleLogOut}
                roles={roles}
                userName={userName}
                activeRoleId={activeRoleId}
                capabilities={capabilities}
                scope={scope}
                prepareAllScopes={prepareAllScopes}
                onSwitchRole={() => setActiveRoleId(null)}
            />
        )
    }

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ width: '100%' }}>
                <Backdrop
                    sx={{ color: '#fff', zIndex: 10000 }}
                    open={pending}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
                <ErrorBoundary>{content()}</ErrorBoundary>
                {secondsLeft !== null && (
                    <SessionExpiryDialog
                        secondsLeft={secondsLeft}
                        busy={stayingLoggedIn}
                        onStay={handleStayLoggedIn}
                        onLogOut={handleLogOut}
                    />
                )}
                {/* Errors stay until they are dismissed. Eight seconds was enough for a mistyped
                    field and far too little for the long ones: an Eventor action is started here
                    and watched from the Eventor tab, so the report of a half-finished correction
                    had come and gone before anyone looked back. Nothing else on this bus is
                    routine either — every message here is a failure. */}
                <Snackbar
                    open={errorMessage !== null}
                    onClose={(_, reason) => { if (reason !== 'clickaway') setErrorMessage(null) }}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ width: '100%' }}>
                        {errorMessage}
                        {errorDetail && <div style={{ fontSize: '0.8em', opacity: 0.8, marginTop: 4 }}>{errorDetail}</div>}
                    </Alert>
                </Snackbar>
            </Box>
        </ThemeProvider>
    )
}
